import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useState } from "react";
import { useFragment } from "react-relay";

import { ChevronMiniDown } from "@whoosh/design-system/src/components/icons_v2/general";
import { ChevronMiniUp } from "@whoosh/design-system/src/components/icons_v2/general/icons/ChevronMiniUp";
import {
  VStack,
  Text,
  HStack,
  Pressable,
} from "@whoosh/design-system/src/components/primitive";

import { AvailableSpotRow } from "../AvailableSpotRow/AvailableSpotRow";
import {
  OtherPlayerRow,
  OtherPlayerRowProps,
} from "../OtherPlayerRow/OtherPlayerRow";
import { YourSpotRow } from "../YourSpotRow/YourSpotRow";
import { OtherPlayersInThisBooking_Booking$key } from "./__generated__/OtherPlayersInThisBooking_Booking.graphql";

export const OtherPlayersInThisBooking = ({
  bookingRef,
}: {
  bookingRef: OtherPlayersInThisBooking_Booking$key | null;
}) => {
  const booking = useFragment(
    OtherPlayersInThisBookingBookingFragmentString,
    bookingRef
  );

  const [isExpanded, setIsExpanded] = useState(true);

  const otherPlayers: OtherPlayerRowProps[] =
    booking?.rounds?.edges?.flatMap((r) =>
      !!r?.node?.player?.fullName
        ? [
            {
              fullName: r.node.player.fullName,
              firstName: r.node.player.firstName ?? "",
              lastName: r.node.player.lastName ?? "",
              holeCount: r.node.courseLayout?.holeCount ?? undefined,
              transportationTypeCode: r.node.transportationType?.code
                ? r.node.transportationType.code
                : r.node.transportationType?.customCode ?? undefined,
              isOwner: r.node.player.id === booking?.ownerClient?.id,
            },
          ]
        : []
    ) ?? [];

  //-1 from length to account for the "your spot".
  const remainingUnknownSlots =
    (booking?.rounds?.edges?.filter((e) => !e?.node?.player).length ?? 1) - 1;

  return (
    <VStack>
      <Pressable onPress={() => setIsExpanded(!isExpanded)}>
        <HStack alignItems="center" justifyContent="space-between">
          <Text
            variant="mainTiny"
            color="text-secondary"
          >{t`OTHER PLAYERS IN THIS BOOKING`}</Text>
          {isExpanded ? (
            <ChevronMiniDown color="grayscale.700" />
          ) : (
            <ChevronMiniUp color="grayscale.700" />
          )}
        </HStack>
      </Pressable>
      {isExpanded ? (
        <VStack paddingTop={6} gap={1}>
          {otherPlayers.map((player) => (
            <OtherPlayerRow
              key={player.fullName}
              firstName={player.firstName}
              lastName={player.lastName}
              holeCount={player.holeCount}
              transportationTypeCode={player.transportationTypeCode}
              isOwner={player.isOwner}
              fullName={player.fullName}
            />
          ))}
          <YourSpotRow />
          {Array.from({ length: remainingUnknownSlots }, (_, index) => (
            <AvailableSpotRow key={index} />
          ))}
        </VStack>
      ) : null}
    </VStack>
  );
};

const OtherPlayersInThisBookingBookingFragmentString = graphql`
  fragment OtherPlayersInThisBooking_Booking on Booking {
    id
    ownerClient {
      ... on Customer {
        id
      }
      ... on Member {
        id
      }
    }
    rounds {
      edges {
        node {
          player {
            id
            fullName
            firstName
            lastName
          }
          courseLayout {
            holeCount
          }
          transportationType {
            code
            customCode
          }
        }
      }
    }
  }
`;
